import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuestionnaire } from './useQuestionnaire';
import { apiRequest } from '../Api';

/**
 * Orchestration hook for LMBA (Lifestyle Medicine Baseline Assessment)
 * Manages the full 7-instrument assessment flow across all pillars
 *
 * @param {number} patientId - Patient ID
 * @param {number} orgId - Organisation ID
 * @returns {Object} { pillars, completedPillars, allScores, history, isLoading, error, methods }
 */
export const useLmba = (patientId, orgId, options = {}) => {
  const { skipHistoryHydration = false } = options;

  // Define the 7 LMBA instruments
  const INSTRUMENTS = useMemo(() => [
    {
      id: 1,
      instrument: 'psqi',
      pillar: 'sleep',
      name: 'Sleep Quality (PSQI)',
      description: 'Pittsburgh Sleep Quality Index',
      icon: '😴',
      color: '#9C27B0',
    },
    {
      id: 2,
      instrument: 'ipaq-sf',
      pillar: 'physical-activity',
      name: 'Physical Activity (IPAQ-SF)',
      description: 'International Physical Activity Questionnaire',
      icon: '🏃',
      color: '#FF5722',
    },
    {
      id: 3,
      instrument: 'predimed-sa',
      pillar: 'nutrition',
      name: 'Diet Quality (PREDIMED-SA)',
      description: 'Mediterranean Diet Assessment',
      icon: '🥗',
      color: '#4CAF50',
    },
    {
      id: 4,
      instrument: 'audit-c',
      pillar: 'substance-use',
      name: 'Alcohol Screening (AUDIT-C)',
      description: 'Alcohol Use Disorders Identification Test',
      icon: '🚫',
      color: '#F44336',
    },
    {
      id: 5,
      instrument: 'stop-bang',
      pillar: 'sleep',
      name: 'Sleep Apnea (STOP-Bang)',
      description: 'Obstructive Sleep Apnea Screening',
      icon: '⚠️',
      color: '#FF9800',
    },
    {
      id: 6,
      instrument: 'ucla',
      pillar: 'social-connection',
      name: 'Loneliness (UCLA)',
      description: 'UCLA Loneliness Scale',
      icon: '🤝',
      color: '#2196F3',
    },
    {
      id: 7,
      instrument: 'sa-contextual',
      pillar: 'barriers',
      name: 'Socioeconomic Barriers',
      description: 'Social & Environmental Barriers',
      icon: '🏘️',
      color: '#795548',
    },
  ], []);

  // Map instrument names to questionnaire IDs in the database
  const INSTRUMENT_ID_MAP = useMemo(() => ({
    'psqi': 1,
    'ipaq-sf': 2,
    'predimed-sa': 3,
    'audit-c': 4,
    'stop-bang': 5,
    'ucla': 6,
    'sa-contextual': 7,
  }), []);

  // Helper to create initial states
  const createInitialStates = useCallback(() => {
    const states = {};
    INSTRUMENTS.forEach((inst) => {
      states[inst.instrument] = {
        status: 'not-started', // not-started, in-progress, completed, scored
        responseId: null,
        score: null,
        error: null,
      };
    });
    return states;
  }, [INSTRUMENTS]);

  // State for each instrument's response
  const [instrumentStates, setInstrumentStates] = useState(createInitialStates);
  const [allScores, setAllScores] = useState({});
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Helper to persist retake progress in session storage
   */
  const persistRetakeProgress = useCallback((instrumentKey, responseId, score = null) => {
    if (!skipHistoryHydration) return;
    
    try {
      const key = `lmba_retake_${patientId}`;
      const existing = JSON.parse(sessionStorage.getItem(key) || '{}');
      existing[instrumentKey] = { responseId, score, status: score ? 'scored' : 'in-progress' };
      sessionStorage.setItem(key, JSON.stringify(existing));
    } catch (e) {
      console.error('Failed to persist retake progress:', e);
    }
  }, [patientId, skipHistoryHydration]);

  /**
   * Fetch LMBA assessment history
   */
  const fetchHistory = useCallback(async () => {
    if (!patientId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiRequest(`/api/patient/${patientId}/lmba-history`, {
        method: 'GET',
      });

      const historyData = response || [];
      setHistory(historyData);

      const newAllScores = {};
      const newInstrumentStates = createInitialStates();

      // If we are NOT in retake mode, populate from history
      if (historyData.length > 0 && !skipHistoryHydration) {
        // Iterate through all history entries from oldest to newest to merge results
        [...historyData].reverse().forEach((assessment) => {
          const scoresMap = assessment.scores || assessment.Scores || {};
          const assessmentId = assessment.assessmentId || assessment.AssessmentId;
          
          Object.entries(scoresMap).forEach(([instrumentKey, scoreResult]) => {
            const instrument = INSTRUMENTS.find(i => i.instrument === instrumentKey || i.id.toString() === instrumentKey);
            if (instrument) {
              newAllScores[instrument.instrument] = scoreResult;

              newInstrumentStates[instrument.instrument] = {
                status: 'scored',
                responseId: assessmentId,
                score: scoreResult,
                error: null,
              };
            }
          });
        });
      }
      
      // If in retake mode, layer on any in-progress retake state from session storage
      if (skipHistoryHydration) {
        try {
          const retakeKey = `lmba_retake_${patientId}`;
          const retakeData = JSON.parse(sessionStorage.getItem(retakeKey) || '{}');
          
          Object.entries(retakeData).forEach(([instKey, data]) => {
            if (newInstrumentStates[instKey]) {
              newInstrumentStates[instKey] = {
                ...newInstrumentStates[instKey],
                status: data.status,
                responseId: data.responseId,
                score: data.score
              };
              if (data.score) {
                newAllScores[instKey] = data.score;
              }
            }
          });
        } catch (e) {
          console.error('Failed to hydrate retake progress:', e);
        }
      }

      setAllScores(newAllScores);
      setInstrumentStates(newInstrumentStates);
    } catch (err) {
      console.error('Failed to fetch LMBA history:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [patientId, skipHistoryHydration, INSTRUMENTS, createInitialStates]); 

  // Fetch history on mount or when patientId changes
  useEffect(() => {
    if (patientId) {
      fetchHistory();
    }
  }, [patientId, fetchHistory]);

  /**
   * Get completion status for all instruments
   */
  const getCompletionStatus = useCallback(() => {
    const status = {
      totalInstruments: INSTRUMENTS.length,
      completed: 0,
      scored: 0,
      inProgress: 0,
      notStarted: 0,
      completionPercentage: 0,
    };

    Object.values(instrumentStates).forEach((state) => {
      if (state.status === 'scored') {
        status.scored++;
        status.completed++;
      } else if (state.status === 'completed') {
        status.completed++;
      } else if (state.status === 'in-progress') {
        status.inProgress++;
      } else {
        status.notStarted++;
      }
    });

    status.completionPercentage = Math.round(
      ((status.completed + status.inProgress) / status.totalInstruments) * 100
    );

    return status;
  }, [instrumentStates, INSTRUMENTS]);

  /**
   * Start assessment for a specific instrument
   */
  const startInstrument = useCallback(
    async (instrument) => {
      setError(null);
      setIsLoading(true);

      try {
        const params = new URLSearchParams();
        params.append('patientId', patientId);
        params.append('orgId', orgId);

        const questionnaireId = INSTRUMENT_ID_MAP[instrument.instrument];
        
        const response = await apiRequest(
          `/api/questionnaires/${questionnaireId}/responses?${params.toString()}`,
          { method: 'POST' }
        );

        setInstrumentStates((prev) => ({
          ...prev,
          [instrument.instrument]: {
            status: 'in-progress',
            responseId: response.responseId,
            score: null,
            error: null,
          },
        }));

        persistRetakeProgress(instrument.instrument, response.responseId);

        return response;
      } catch (err) {
        setError(err.message);
        setInstrumentStates((prev) => ({
          ...prev,
          [instrument.instrument]: {
            ...prev[instrument.instrument],
            error: err.message,
          },
        }));
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [patientId, orgId, INSTRUMENT_ID_MAP, persistRetakeProgress]
  );

  /**
   * Complete and score an instrument
   */
  const completeAndScoreInstrument = useCallback(
    async (instrument, responseId) => {
      setError(null);
      setIsLoading(true);

      try {
        await apiRequest(
          `/api/questionnaires/responses/${responseId}/complete`,
          { method: 'POST' }
        );

        const scoreResult = await apiRequest(
          `/api/questionnaires/${responseId}/score`,
          { method: 'POST' }
        );

        setInstrumentStates((prev) => ({
          ...prev,
          [instrument.instrument]: {
            status: 'scored',
            responseId,
            score: scoreResult,
            error: null,
          },
        }));

        setAllScores((prev) => ({
          ...prev,
          [instrument.instrument]: scoreResult,
        }));

        persistRetakeProgress(instrument.instrument, responseId, scoreResult);

        return scoreResult;
      } catch (err) {
        setError(err.message);
        setInstrumentStates((prev) => ({
          ...prev,
          [instrument.instrument]: {
            ...prev[instrument.instrument],
            error: err.message,
          },
        }));
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [persistRetakeProgress]
  );

  /**
   * Resume an assessment that was started previously
   */
  const resumeInstrument = useCallback(
    async (instrument, responseId) => {
      setError(null);
      setIsLoading(true);

      try {
        const response = await apiRequest(
          `/api/questionnaires/responses/${responseId}`,
          { method: 'GET' }
        );

        const status =
          response.status === 'completed'
            ? 'completed'
            : response.status === 'scored'
              ? 'scored'
              : 'in-progress';

        const score = response.score || null;

        setInstrumentStates((prev) => ({
          ...prev,
          [instrument.instrument]: {
            status,
            responseId,
            score,
            error: null,
          },
        }));

        persistRetakeProgress(instrument.instrument, responseId, score);

        return response;
      } catch (err) {
        setError(err.message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [persistRetakeProgress]
  );

  /**
   * Calculate aggregate scores across all pillars
   */
  const getAggregateScores = useCallback(() => {
    const aggregates = {};

    Object.entries(allScores).forEach(([pillar, score]) => {
      const pillarScores = score?.pillarScores || score?.PillarScores;
      if (pillarScores) {
        Object.entries(pillarScores).forEach(([key, value]) => {
          aggregates[key] = value;
        });
      }
    });

    return aggregates;
  }, [allScores]);

  /**
   * Get pillar status
   */
  const getPillarStatus = useCallback(
    (pillar) => {
      const instrument = INSTRUMENTS.find((i) => i.pillar === pillar);
      if (!instrument) return null;

      return {
        ...instrument,
        ...instrumentStates[instrument.instrument],
        completionStatus: getCompletionStatus(),
      };
    },
    [instrumentStates, getCompletionStatus, INSTRUMENTS]
  );

  /**
   * Get all pillar statuses
   */
  const getAllPillarStatuses = useCallback(() => {
    return INSTRUMENTS.map((instrument) => ({
      ...instrument,
      ...instrumentStates[instrument.instrument],
    }));
  }, [instrumentStates, INSTRUMENTS]);

  /**
   * Mark an instrument as scored without making API calls
   */
  const markInstrumentScored = useCallback((instrument, responseId, score) => {
    setInstrumentStates((prev) => ({
      ...prev,
      [instrument.instrument]: {
        status: 'scored',
        responseId,
        score,
        error: null,
      },
    }));
    setAllScores((prev) => ({
      ...prev,
      [instrument.instrument]: score,
    }));

    persistRetakeProgress(instrument.instrument, responseId, score);
  }, [persistRetakeProgress]);

  /**
   * Reset LMBA assessment
   */
  const reset = useCallback(() => {
    setInstrumentStates(createInitialStates());
    setAllScores({});
    setHistory([]);
    setError(null);
    
    // Clear retake progress on full reset
    try {
      sessionStorage.removeItem(`lmba_retake_${patientId}`);
    } catch (e) {}
  }, [patientId, createInitialStates]);

  return {
    // Data
    instrumentStates,
    allScores,
    history,
    isLoading,
    error,
    INSTRUMENTS,

    // Status
    completionStatus: getCompletionStatus(),
    aggregateScores: getAggregateScores(),

    // Methods
    methods: {
      startInstrument,
      completeAndScoreInstrument,
      resumeInstrument,
      fetchHistory,
      getPillarStatus,
      getAllPillarStatuses,
      markInstrumentScored,
      reset,
    },
  };
};

export default useLmba;
